import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { type FAQItemType } from "@vega-core/models/faq.type";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { EmptyCard, type EmptyCardProps } from "../Cards/EmptyCard/EmptyCard";

import { FAQItem } from "./FAQItem";
import { FAQItemFull } from "./FAQItemFull";
import { FAQListSkeleton } from "./FAQListSkeleton";

export type FAQListProps = {
  enableHashHighlight?: boolean;
  data?: FAQItemType[];
  emptyState?: EmptyCardProps;
  fixed?: boolean;
  isFull?: boolean;
  isLoading?: boolean;
  listIndex?: number;
  openList?: number;
  setOpenList?: (value: number | undefined) => void;
  withBorders?: boolean;
};

export function FAQList({
  enableHashHighlight = false,
  data,
  emptyState,
  isFull,
  isLoading,
  listIndex,
  openList,
  setOpenList,
  withBorders,
}: FAQListProps) {
  const [open, setOpen] = useState<number>();
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const location = useLocation();

  useEffect(() => {
    if (
      listIndex !== undefined &&
      openList !== undefined &&
      openList !== listIndex
    ) {
      setOpen(undefined);
    }

    if (enableHashHighlight) {
      const hash = decodeURIComponent(location.hash.slice(1));

      if (hash && data) {
        const matchIndex = data.findIndex((item) => item.id === hash);

        if (matchIndex !== -1) {
          setOpen(matchIndex);
          setHighlightIndex(matchIndex);
        }
      }
    }

    if (!enableHashHighlight || highlightIndex === null) {
      return;
    }

    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }

    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightIndex(null);
    }, 500);

    return () => clearTimeout(highlightTimeoutRef.current ?? undefined);
  }, [
    data,
    enableHashHighlight,
    highlightIndex,
    location.hash,
    listIndex,
    openList,
  ]);

  const setOpenItem = (value: number | undefined) => {
    setOpen(value);

    if (setOpenList) {
      setOpenList(listIndex);
    }
  };

  if (isTruthy(isLoading)) {
    return <FAQListSkeleton />;
  }

  const hasData = isTruthy(data) && data.length > 0;

  if (!hasData && emptyState) {
    return <EmptyCard {...emptyState} />;
  }

  return (
    <div
      className={cnTwMerge("w-full", {
        "divide-y-1 divide-border/primary border-y-1 border-border/primary ":
          withBorders,
      })}
    >
      {data
        ? data.map((item, index) =>
            isTruthy(isFull) ? (
              <FAQItemFull
                highlight={highlightIndex === index}
                id={item.id}
                index={index}
                key={item.title}
                open={open}
                setOpen={setOpenItem}
                {...item}
              />
            ) : (
              <FAQItem
                highlight={highlightIndex === index}
                id={item.id}
                index={index}
                key={item.title}
                open={open}
                setOpen={setOpenItem}
                {...item}
              />
            ),
          )
        : null}
    </div>
  );
}
